import React, { useEffect, useState } from 'react';
import { addEducation, updateEducation, deleteEducation } from '../../services/profile';
import './Education.css'; // We'll create this CSS file
import { FaGraduationCap, FaEdit, FaTrashAlt, FaPlus, FaSave, FaTimes } from 'react-icons/fa'; // Import icons

const Education = ({ educationList, profileData, setEducationList, profileSection, setCompletion }) => {

  useEffect(() => {
    setCompletion("education", educationList);
  }, [educationList])


  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);


  // State for new/editing education
  const [educationForm, setEducationForm] = useState({
    institutionName: '',
    degree: '',
    fieldOfStudy: '',
    startDate: '',
    endDate: '',
    current: false,
    gpa: '',
    coursework: ''
  });

  // Clear form data
  const clearForm = () => {
    setEducationForm({
      institutionName: '',
      degree: '',
      fieldOfStudy: '',
      startDate: '',
      endDate: '',
      current: false,
      gpa: '',
      coursework: ''
    });
    setFormError('');
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEducationForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // If setting current education to true, clear end date
    if (name === 'current' && checked) {
      setEducationForm(prev => ({
        ...prev,
        endDate: ''
      }));
    }
  };

  // Add new education
  const handleAddEducation = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!educationForm.institutionName || !educationForm.degree || !educationForm.startDate ||
      (!educationForm.endDate && !educationForm.current)) {
      setFormError('Please fill out all required fields');
      return;
    }

    // Prepare data for API
    const educationData = {
      institution_name: educationForm.institutionName,
      degree: educationForm.degree,
      field_of_study: educationForm.fieldOfStudy || null,
      start_date: educationForm.startDate,
      end_date: educationForm.current ? null : educationForm.endDate,
      gpa: educationForm.gpa || null,
      coursework: educationForm.coursework || null,
    };

    try {
      setIsSubmitting(true);

      // Send to API
      const response = await addEducation(educationData, profileData?.profile_id);

      // Add the returned education (with real ID) to the list
      setEducationList([...educationList, {
        id: response.id,
        institutionName: response.institution_name,
        degree: response.degree,
        fieldOfStudy: response.field_of_study,
        startDate: response.start_date,
        endDate: response.end_date,
        current: !response.end_date,
        gpa: response.gpa,
        coursework: response.coursework
      }]);

      // Reset form and state
      clearForm();
      setIsAdding(false);
    } catch (error) {
      // Handle API error
      console.error("Failed to add education:", error);
      setFormError(error.message || 'Failed to save education. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start editing an education
  const handleEditStart = (education) => {
    setIsEditing(education.id);
    setEducationForm({ ...education });
  };

  // Save edited education
  const handleEditSave = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!educationForm.institutionName || !educationForm.degree || !educationForm.startDate ||
      (!educationForm.endDate && !educationForm.current)) {
      setFormError('Please fill out all required fields');
      return;
    }

    // Prepare data for API
    const educationData = {
      institution_name: educationForm.institutionName,
      degree: educationForm.degree,
      field_of_study: educationForm.fieldOfStudy || null,
      start_date: educationForm.startDate,
      end_date: educationForm.current ? null : educationForm.endDate,
      gpa: educationForm.gpa || null,
      coursework: educationForm.coursework || null,
    };

    try {
      setIsSubmitting(true);

      // Update via API
      const response = await updateEducation(isEditing, educationData, profileData?.profile_id);

      // Update the education in the local list
      setEducationList(educationList.map(edu =>
        edu.id === isEditing ? {
          id: response.id,
          institutionName: response.institution_name,
          degree: response.degree,
          fieldOfStudy: response.field_of_study,
          startDate: response.start_date,
          endDate: response.end_date,
          current: !response.end_date,
          gpa: response.gpa,
          coursework: response.coursework
        } : edu
      ));

      // Reset form and state
      clearForm();
      setIsEditing(null);
    } catch (error) {
      // Handle API error
      console.error("Failed to update education:", error);
      setFormError(error.message || 'Failed to update education. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    clearForm();
    setIsAdding(false);
    setIsEditing(null);
  };

  // Delete education
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this education entry?')) {
      try {
        setIsSubmitting(true);

        // Delete via API
        await deleteEducation(id, profileData?.profile_id);

        // Remove from local list
        setEducationList(educationList.filter(edu => edu.id !== id));
      } catch (error) {
        console.error("Failed to delete education:", error);
        alert('Failed to delete education. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="education-section">
      <div className="section-header">
        <h2><div className="section-icon">
          {profileSection.icon}
        </div> Education</h2>
        {!isAdding && !isEditing && (
          <button
            onClick={() => setIsAdding(true)}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            <FaPlus className="button-icon" /> Add Education
          </button>
        )}
      </div>

      {/* Education List */}
      {educationList.length > 0 ? (
        <div className="education-list">
          {educationList.map((education) => (
            <div key={education.id} className="education-item">
              {isEditing === education.id ? (
                <form onSubmit={handleEditSave} className="education-form">
                  <h3>Edit Education</h3>
                  {formError && <div className="form-error">{formError}</div>}

                  <div className="form-group">
                    <label htmlFor="institutionName">Institution Name *</label>
                    <input
                      type="text"
                      id="institutionName"
                      name="institutionName"
                      value={educationForm.institutionName}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="degree">Degree *</label>
                      <input
                        type="text"
                        id="degree"
                        name="degree"
                        value={educationForm.degree}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Bachelor of Science, Master's"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="fieldOfStudy">Field of Study</label>
                      <input
                        type="text"
                        id="fieldOfStudy"
                        name="fieldOfStudy"
                        value={educationForm.fieldOfStudy}
                        onChange={handleInputChange}
                        placeholder="e.g., Computer Science"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="startDate">Start Date *</label>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        value={educationForm.startDate}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="endDate">End Date {!educationForm.current && '*'}</label>
                      <input
                        type="date"
                        id="endDate"
                        name="endDate"
                        value={educationForm.endDate}
                        onChange={handleInputChange}
                        disabled={educationForm.current || isSubmitting}
                        required={!educationForm.current}
                      />
                    </div>
                  </div> */}

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="startDate">Start Date *</label>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        value={educationForm.startDate}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="date-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="endDate">End Date {!educationForm.current && '*'}</label>
                      <input
                        type="date"
                        id="endDate"
                        name="endDate"
                        value={educationForm.endDate}
                        onChange={handleInputChange}
                        disabled={educationForm.current || isSubmitting}
                        className="date-input"
                      />
                    </div>

                    <div className="form-group checkbox">
                      <div className="current-checkbox">
                        <input
                          type="checkbox"
                          id="current"
                          name="current"
                          checked={educationForm.current}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        <label htmlFor="current">I'm currently studying here</label>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="gpa">GPA</label>
                    <input
                      type="text"
                      id="gpa"
                      name="gpa"
                      value={educationForm.gpa}
                      onChange={handleInputChange}
                      placeholder="e.g., 3.8/4.0"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="coursework">Relevant Coursework</label>
                    <textarea
                      id="coursework"
                      name="coursework"
                      value={educationForm.coursework}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="List relevant courses separated by commas"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn-secondary"
                      disabled={isSubmitting}
                    >
                      <FaTimes className="button-icon" /> Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ?
                        'Saving...' :
                        <><FaSave className="button-icon" /> Save Changes</>
                      }
                    </button>
                  </div>
                </form>
              ) : (
                <div className="education-display">
                  <div className="education-header">
                    <div>
                      <h3>{education.institutionName}</h3>
                      <h4>
                        {education.degree}
                        {education.fieldOfStudy && ` in ${education.fieldOfStudy}`}
                      </h4>
                      <p className="education-dates">
                        {formatDate(education.startDate)} - {education.current ? 'Present' : formatDate(education.endDate)}
                      </p>
                    </div>
                    <div className="education-actions">
                      <button
                        onClick={() => handleEditStart(education)}
                        className="btn-icon"
                        title="Edit"
                        disabled={isSubmitting}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(education.id)}
                        className="btn-icon danger"
                        title="Delete"
                        disabled={isSubmitting}
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>

                  <div className="education-details">
                    {education.gpa && (
                      <p className="education-gpa">
                        <strong>GPA:</strong> {education.gpa}
                      </p>
                    )}

                    {education.coursework && (
                      <div className="education-coursework">
                        <h5>Relevant Coursework:</h5>
                        <p>{education.coursework}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-list">
          <p>No education entries added yet.</p>
        </div>
      )}

      {/* Add Education Form */}
      {isAdding && (
        <div className="add-education">
          <form onSubmit={handleAddEducation} className="education-form">
            <h3>Add New Education</h3>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="institutionName">Institution Name *</label>
              <input
                type="text"
                id="institutionName"
                name="institutionName"
                value={educationForm.institutionName}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="degree">Degree *</label>
                <input
                  type="text"
                  id="degree"
                  name="degree"
                  value={educationForm.degree}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Bachelor of Science, Master's"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="fieldOfStudy">Field of Study</label>
                <input
                  type="text"
                  id="fieldOfStudy"
                  name="fieldOfStudy"
                  value={educationForm.fieldOfStudy}
                  onChange={handleInputChange}
                  placeholder="e.g., Computer Science"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={educationForm.startDate}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date {!educationForm.current && '*'}</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={educationForm.endDate}
                  onChange={handleInputChange}
                  disabled={educationForm.current || isSubmitting}
                  required={!educationForm.current}
                />
              </div>

              <div className="form-group checkbox">
                <div className="current-checkbox">
                  <input
                    type="checkbox"
                    id="current"
                    name="current"
                    checked={educationForm.current}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="current">I'm currently studying here</label>
                </div>
              </div>
            </div>



            <div className="form-group">
              <label htmlFor="gpa">GPA</label>
              <input
                type="text"
                id="gpa"
                name="gpa"
                value={educationForm.gpa}
                onChange={handleInputChange}
                placeholder="e.g., 3.8/4.0"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="coursework">Relevant Coursework</label>
              <textarea
                id="coursework"
                name="coursework"
                value={educationForm.coursework}
                onChange={handleInputChange}
                rows="4"
                placeholder="List relevant courses separated by commas"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                <FaTimes className="button-icon" /> Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ?
                  'Saving...' :
                  <><FaPlus className="button-icon" /> Add Education</>
                }
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Education;